import axios from 'axios';

import type { UserModel } from '~/components/types/user-types';
import { isUserImpersonated } from '~/UserUtils';
import { isProductionEnv, reportErrorInProductionOrThrow } from '~/Utils';

export interface MarkAsReadOptions {
  communicationId: number; //The communication ID to mark as read
  user: UserModel; //Current user object to check for impersonation status
  communicationsReadStatusDict?: Record<string, boolean>; //read statuses to check if already read
  onRefresh?: () => Promise<void> | void; //Callback function to refresh data after marking as read
  throwOnError?: boolean;
}

/**
 * Marks a communication as read with proper validation and error handling.
 * This utility encapsulates the common logic used across different communication components.
 *
 * @param options - Configuration options for marking communication as read
 * @returns Promise that resolves when the operation completes
 */
export async function markCommunicationAsRead(options: MarkAsReadOptions): Promise<void> {
  const { communicationId, user, communicationsReadStatusDict, onRefresh, throwOnError = false } = options;

  if (!communicationId) return;

  try {
    if (communicationsReadStatusDict?.[communicationId]) return;
    if (isProductionEnv() && isUserImpersonated(user)) return;

    await axios.post(`/api/v1/communications/${communicationId}/mark_as_read`);

    // Refresh data if callback provided
    if (onRefresh) {
      await onRefresh();
    }
  } catch (error) {
    if (throwOnError) {
      reportErrorInProductionOrThrow(error);
    }
    // For non-critical functionality, silently handle errors
    // This prevents mark-as-read failures from disrupting the main user flow
  }
}
